import { Button, IconButton, Stack, Tooltip, Typography, useColorScheme, useMediaQuery, useTheme } from '@mui/material';
import GenericConnectionSvg from '../../Icons/Connection/generic-connection.svg';
import GenericConnectionSvgDark from '../../Icons/Connection/generic-connection-dark.svg';
import { isNil } from 'ramda';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import usePowerStateIcon from '../../../hooks/ui/usePowerStateIcon';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, RobotActions } from '../../../utils/constants';
import WirelessIcon from '../../Icons/Connection/WirelessIcon';
import WiredIcon from '../../Icons/Connection/WiredIcon';

const { STATE } = RobotActions;
const { CONNECTED, CONNECTION_TYPE } = RobotActions.STATE;
const { BLUETOOTH_WARNING_DIALOG } = ApplicationActions.Dialogs;

const ConnectButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { mode, systemMode } = useColorScheme();
  const colorMode = mode === 'system' ? systemMode : mode;

  // Responsive breakpoint - switch to icon-only below 1200px (matches bottom bar buttons)
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(800));

  const { robotState, connectionType } = useSelector((state: RootState) => state.robot);
  const { bluetoothAdvertisingName } = useSelector((state: RootState) => state.robotData);
  const { userAgentInfo, enableToolBar } = useSelector((state: RootState) => state.application) as {
    userAgentInfo: { isBrowserSupported: boolean } | null;
    enableToolBar: boolean;
  };
  const openDrawer = useAppSelector((state) => state.project.openDrawer);

  // Connection state
  const [connectedName, setConnectedName] = useState('');
  const powerStateIcon = usePowerStateIcon(colorMode === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    if (robotState !== STATE.CONNECTED || isNil(bluetoothAdvertisingName) || !bluetoothAdvertisingName) {
      setConnectedName(t('button.connect', 'Connect'));
    } else {
      setConnectedName(bluetoothAdvertisingName);
    }
  }, [bluetoothAdvertisingName, robotState, t]);

  // Connection handlers
  const handleConnectClick = () => {
    if (robotState === STATE.CONNECTED) {
      const connectedDrawerAction = openDrawer === ApplicationActions.Drawer.CONNECTED_DRAWER ? null : ApplicationActions.Drawer.CONNECTED_DRAWER;
      dispatch(projectSlice.actions.onOpenDrawer(connectedDrawerAction));
    } else if (userAgentInfo?.isBrowserSupported) {
      const connectionDrawerAction = openDrawer === ApplicationActions.Drawer.CONNECTION_DRAWER ? null : ApplicationActions.Drawer.CONNECTION_DRAWER;
      dispatch(projectSlice.actions.onOpenDrawer(connectionDrawerAction));
    } else {
      dispatch(applicationSlice.actions.onOpenDialog(BLUETOOTH_WARNING_DIALOG));
    }
  };

  const isConnected = robotState === CONNECTED;

  const connectionContent =
    isConnected && powerStateIcon ? (
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
        {powerStateIcon}
        {connectionType === CONNECTION_TYPE.BLE ? (
          <WirelessIcon sx={{ fontSize: 24 }} />
        ) : connectionType === CONNECTION_TYPE.SERIAL ? (
          <WiredIcon sx={{ fontSize: 24 }} />
        ) : null}
      </Stack>
    ) : !isConnected ? (
      <img src={colorMode === 'dark' ? GenericConnectionSvg : GenericConnectionSvgDark} alt="" width={20} height={20} />
    ) : null;

  return isSmallScreen ? (
    // Icon-only view for small screens
    <Tooltip title={connectedName}>
      <span>
        <IconButton
          onClick={handleConnectClick}
          disabled={!enableToolBar}
          sx={{
            backgroundColor: theme.vars?.palette?.accent?.opaque,
            '&:hover': {
              backgroundColor: theme.vars?.palette?.action?.hover,
            },
          }}
        >
          {connectionContent}
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    // Full button view: [power][USB/BT] startIcon + robot name when connected
    <Button
      onClick={handleConnectClick}
      variant="text"
      startIcon={connectionContent}
      disabled={!enableToolBar}
      sx={{
        backgroundColor: theme.vars?.palette?.accent?.opaque,
        textTransform: 'none',
        '&:hover': {
          backgroundColor: theme.vars?.palette?.action?.hover,
        },
      }}
    >
      <Typography variant="buttonText">{connectedName}</Typography>
    </Button>
  );
};

export default ConnectButton;
